import {
  Ion,
  Viewer as CesiumViewer,
  Cartesian3,
  Cartographic,
  Math as CesiumMath,
  Entity,
  Color,
  ScreenSpaceEventType,
  defined,
  sampleTerrain,
  CesiumTerrainProvider,
  viewerDragDropMixin,
  VerticalOrigin,
  HorizontalOrigin,
  createGooglePhotorealistic3DTileset,
  ConstantProperty,
} from "cesium";
import {
  CesiumComponentRef,
  ScreenSpaceEvent,
  ScreenSpaceEventHandler,
  Viewer,
} from "resium";
import { useContext, useEffect, useRef, useState } from "react";
import { environment } from "../../../common/env";
import { Themes } from "../../enums/themes";
import Context from "../utils/context";
import { IMap, IMapPin } from "./models";
import { MapSettings } from "../resiumMap/mapSettings";

export const Map = (props: IMap) => {
  Ion.defaultAccessToken = environment.Cesium_Default_Access_Token;

  const ref = useRef<CesiumComponentRef<CesiumViewer>>(null);

  const context = useContext(Context);

  const [terrainProvider, setTerrainProvider] =
    useState<CesiumTerrainProvider>();
  const [latitude, setLatitude] = useState<number>();
  const [longitude, setLongitude] = useState<number>();
  const [enable3DMap, setEnable3DMap] = useState<boolean>(false);
  const [isShowLables, setIsShowLables] = useState<boolean>(true);

  const pin = useRef<Entity | null>(null);

  useEffect(() => {
    position();
    if (props.locations !== undefined && props.locations.length > 0) {
      applyPins();
    }
  }, [props.locations]);

  useEffect(() => {
    if (props.showPins === false) {
      removePins();
    }
  }, [props.showPins]);

  useEffect(() => {
    if (latitude && longitude) {
      props.viewportLatitude?.(latitude);
      props.viewportLongitude?.(longitude);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    const pinIds = props.locations?.map((loc) => loc.id);
    const entity = props.locations?.find((x) => x.id === props.flyTo);
    if (entity) {
      if (ref.current && ref.current.cesiumElement) {
        const pinViewers = ref.current?.cesiumElement;
        pinIds?.forEach((id) => {
          const singleEntity = pinViewers.entities.getById(id);
          if (singleEntity?.billboard) {
            singleEntity.billboard.scale = new ConstantProperty(1);
            singleEntity.billboard.image = new ConstantProperty(
              "/img/pin_blue_2.png"
            );
          }
        });
        const pinEntity = pinViewers.entities.getById(entity.id);
        if (pinEntity && pinEntity.billboard) {
          pinEntity.billboard.scale = new ConstantProperty(1.5);
          pinEntity.billboard.image = new ConstantProperty("/img/pin_blue.png");
        }
        ref.current.cesiumElement.camera.flyTo({
          destination: Cartesian3.fromDegrees(
            entity.longitude,
            entity.latitude,
            12000
          ),
          duration: 2,
        });
      }
    }
  }, [props.flyTo]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;
      viewer.baseLayerPicker.viewModel.selectedImagery =
        viewer.baseLayerPicker.viewModel.imageryProviderViewModels[0];

      viewer.selectedEntityChanged.addEventListener(function (selectedEntity) {
        if (defined(selectedEntity)) {
          props.selected?.(selectedEntity.id);
          if (defined(viewer)) {
            viewer.selectedEntity = selectedEntity;
          }
        }
      });
    }
  }, [ref.current?.cesiumElement]);

  useEffect(() => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      viewer.baseLayerPicker.viewModel.selectedImagery = isShowLables
        ? viewer.baseLayerPicker.viewModel.imageryProviderViewModels[1]
        : viewer.baseLayerPicker.viewModel.imageryProviderViewModels[0];
    }
  }, [ref.current?.cesiumElement, isShowLables]);

  const removePins = () => {
    if (ref.current && ref.current.cesiumElement) {
      ref.current.cesiumElement.entities.removeAll();
    }
  };
  const applyPins = () => {
    props.locations
      ?.filter(
        (value, index, array) =>
          index === array.findIndex((item) => item.id === value.id)
      )
      ?.map((location) => {
        if (ref.current && ref.current.cesiumElement) {
          pin.current = ref.current?.cesiumElement?.entities.add({
            id: location.id,
            position: Cartesian3.fromDegrees(
              location.longitude,
              location.latitude,
              location.altitude
            ),
            billboard: {
              image: "/img/pin_blue_2.png",
              width: 64,
              height: 64,
              color: Color.WHITE.withAlpha(1.0),
              verticalOrigin: VerticalOrigin.BOTTOM,
              horizontalOrigin: HorizontalOrigin.CENTER,
              eyeOffset: new Cartesian3(0, 0, -100),
            },
          });
        }
      });
  };

  const position = async () => {
    if (ref.current && ref.current.cesiumElement) {
      setLongitude(context.currentPosition.longitude);
      setLatitude(context.currentPosition.latitude);

      ref.current.cesiumElement.camera.flyTo({
        destination: Cartesian3.fromDegrees(
          context.currentPosition.longitude,
          context.currentPosition.latitude,
          context.currentPosition.altitude || 12000
        ),
        duration: 4,
      });
    }
  };

  const OnMouseLeftUp = (e: any) => {
    if (ref.current && ref.current.cesiumElement) {
      var position = ref.current.cesiumElement.camera.positionCartographic;
      if (position) {
        setLongitude(position.longitude * (180 / Math.PI));
        setLatitude(position.latitude * (180 / Math.PI));
      }
    }
  };

  const OnMouseRightClick = (e: any) => {
    if (!ref.current || !ref.current.cesiumElement) return;

    const viewer = ref.current.cesiumElement;

    var ray = viewer.camera.getPickRay(e.position);
    if (!ray) return;

    var position = viewer.scene.globe.pick(ray, viewer.scene);

    if (defined(position)) {
      var cartographic = Cartographic.fromCartesian(position);
      const latitude = CesiumMath.toDegrees(cartographic.latitude);
      const longitude = CesiumMath.toDegrees(cartographic.longitude);

      sampleTerrain(viewer.terrainProvider, 11, [cartographic]).then(
        (updatedPositions) => {
          const height = updatedPositions[0].height;

          if (height) {
            if (pin.current) {
              viewer.entities.remove(pin.current);
            }

            const position = Cartesian3.fromDegrees(
              longitude,
              latitude,
              height
            );

            // Add a new pin (entity) at the clicked position
            pin.current = viewer.entities.add({
              position: position,
              billboard: {
                image: "/img/pin_blue.png",
                width: 32,
                height: 32,
                color: Color.WHITE.withAlpha(1.0),
                eyeOffset: new Cartesian3(0, 0, -100),
              },
            });

            if (props.onClick) {
              const details: IMapPin = {
                id: "",
                latitude: latitude,
                longitude: longitude,
                altitude: height,
              };
              props.onClick?.(details);
            }
          }
        }
      );
    }
  };

  const onEntityClick = (e: any) => {
    if (ref.current && ref.current.cesiumElement) {
      var pickedObject = ref.current.cesiumElement.scene.pick(e.position);

      if (defined(pickedObject) && pickedObject.id?._id) {
        props.onPinClick?.(pickedObject.id._id);
      }

      if (props.setPostSelected != null) {
        props.setPostSelected("");
      }
    }
  };

  const handleShowLablesToggle = () => setIsShowLables(!isShowLables);

  const handleEnable3DMapToggle = async () => {
    if (ref.current && ref.current.cesiumElement) {
      const viewer = ref.current.cesiumElement;

      if (viewer.scene.primitives.length > 0) {
        viewer.scene.primitives.removeAll();

        setEnable3DMap(false);
      } else {
        const tileset = await createGooglePhotorealistic3DTileset({
          key: environment.Google_Api_Key,
        });
        viewer.scene.primitives.add(tileset);

        setEnable3DMap(true);
      }
    }
  };

  return (
    <>
      <div
        className="fixed top-20 left-10 w-[300px] bg-transparent flex flex-col gap-2"
        style={{ zIndex: 10 }}
      >
        <MapSettings
          onShowLablesToggle={handleShowLablesToggle}
          onEnable3DMapToggle={handleEnable3DMapToggle}
          isEnable3DMap={enable3DMap}
          isShowLables={isShowLables}
        />
      </div>
      <Viewer
        className={`relative h-full ${props.className ?? "w-full"}`}
        ref={ref}
        animation={false}
        timeline={false}
        homeButton={true}
        sceneModePicker={false}
        baseLayerPicker={true}
        navigationHelpButton={false}
        shouldAnimate={true}
        requestRenderMode={true}
        terrainProvider={terrainProvider}
        extend={viewerDragDropMixin}
      >
        <ScreenSpaceEventHandler>
          <ScreenSpaceEvent
            action={(e) => OnMouseLeftUp(e)}
            type={ScreenSpaceEventType.MIDDLE_CLICK}
          />
          <ScreenSpaceEvent
            action={(e) => OnMouseRightClick(e)}
            type={ScreenSpaceEventType.RIGHT_CLICK}
          />
          <ScreenSpaceEvent
            action={(e) => onEntityClick(e)}
            type={ScreenSpaceEventType.LEFT_CLICK}
          />
        </ScreenSpaceEventHandler>
      </Viewer>
    </>
  );
};
